import { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { FileText, Image, Eye, Download, ExternalLink, FolderOpen, Loader2 } from "lucide-react";
// 29/09/2026 — date gg/mm/aaaa da un'unica fonte (qui c'era formatDateShort, "29 set").
import { formatDate } from "../lib/format";

const API = process.env.REACT_APP_BACKEND_URL;

/** Get icon based on mime type */
function DocIcon({ mime, className = "h-8 w-8" }) {
  if (mime?.startsWith("image/")) return <Image className={`${className} text-blue-500`} />;
  if (mime === "application/pdf") return <FileText className={`${className} text-red-500`} />;
  return <FileText className={`${className} text-muted-foreground`} />;
}

/** Format file size */
function formatSize(bytes) {
  if (!bytes) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1048576) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / 1048576).toFixed(1)} MB`;
}

export function DocumentPreviewCard({ entityType, entityId, entityName, onOpenAll, maxItems = 4 }) {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (entityType && entityId) fetchDocuments();
  }, [entityType, entityId]);

  const fetchDocuments = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`/api/documents?entity_type=${entityType}&entity_id=${entityId}`);
      setDocuments(res.data || []);
    } catch {
      setDocuments([]);
    } finally {
      setLoading(false);
    }
  };

  const handlePreview = (doc) => {
    if (doc.drive_url) {
      window.open(doc.drive_url, "_blank");
    } else if (doc.local_path) {
      window.open(`${API}/api/documents/preview/${doc.document_id}`, "_blank");
    }
  };

  const handleDownload = async (doc) => {
    try {
      const response = await axios.get(`/api/documents/download/${doc.document_id}`, {
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", doc.filename || "documento");
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch {
      // fallback to drive url
      if (doc.drive_url) window.open(doc.drive_url, "_blank");
    }
  };

  if (loading) {
    return (
      <Card data-testid="doc-preview-loading">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
            <FolderOpen className="h-4 w-4" /> Documenti
          </CardTitle>
        </CardHeader>
        <CardContent className="flex items-center justify-center py-4">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  const visibleDocs = documents.slice(0, maxItems);
  const hiddenCount = documents.length - maxItems;

  return (
    <Card data-testid="doc-preview-card">
      <CardHeader className="pb-2 flex flex-row items-center justify-between">
        <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
          <FolderOpen className="h-4 w-4" /> Documenti ({documents.length})
        </CardTitle>
        {documents.length > 0 && onOpenAll && (
          <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={onOpenAll} data-testid="open-all-docs-btn">
            Vedi tutti
          </Button>
        )}
      </CardHeader>
      <CardContent>
        {documents.length === 0 ? (
          <p className="text-xs text-muted-foreground text-center py-3" data-testid="no-docs-msg">
            Nessun documento caricato
          </p>
        ) : (
          <div className="space-y-1.5">
            {visibleDocs.map((doc) => (
              <div
                key={doc.document_id}
                className="group flex items-center gap-2.5 p-2 rounded-md hover:bg-accent/50 transition-colors cursor-pointer"
                onClick={() => handlePreview(doc)}
                data-testid={`doc-preview-item-${doc.document_id}`}
              >
                {/* Icon / thumbnail */}
                {doc.mime_type?.startsWith("image/") && doc.local_path ? (
                  <div className="h-9 w-9 rounded overflow-hidden bg-muted flex-shrink-0">
                    <img
                      src={`${API}/api/documents/preview/${doc.document_id}`}
                      alt=""
                      className="h-full w-full object-cover"
                      onError={(e) => { e.target.style.display = "none"; e.target.parentElement.innerHTML = '<div class="h-full w-full flex items-center justify-center"><svg class="h-5 w-5 text-blue-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><path d="m21 15-5-5L5 21"/></svg></div>'; }}
                    />
                  </div>
                ) : (
                  <div className="h-9 w-9 rounded bg-muted flex items-center justify-center flex-shrink-0">
                    <DocIcon mime={doc.mime_type} className="h-5 w-5" />
                  </div>
                )}

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium truncate leading-tight">
                    {doc.document_type_label || doc.document_type || doc.filename}
                  </p>
                  <p className="text-[10px] text-muted-foreground">
                    {formatDate(doc.created_at)}
                    {doc.file_size ? ` · ${formatSize(doc.file_size)}` : ""}
                  </p>
                </div>

                {/* Actions (visible on hover) */}
                <div className="flex gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                  {doc.drive_url && (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6"
                      onClick={(e) => { e.stopPropagation(); window.open(doc.drive_url, "_blank"); }}
                      title="Apri su MEGA"
                    >
                      <ExternalLink className="h-3 w-3" />
                    </Button>
                  )}
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6"
                    onClick={(e) => { e.stopPropagation(); handleDownload(doc); }}
                    title="Scarica"
                  >
                    <Download className="h-3 w-3" />
                  </Button>
                </div>
              </div>
            ))}

            {hiddenCount > 0 && (
              <Button
                variant="ghost"
                size="sm"
                className="w-full h-7 text-xs text-muted-foreground"
                onClick={onOpenAll}
              >
                + altri {hiddenCount} documenti
              </Button>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default DocumentPreviewCard;
